/**
 * v0.96.21 · Campanhas recorrentes — UI standalone.
 *
 * Gerenciamento de recorrências (cria/lista/pausa/reativa/deleta). Reusa
 * o componente `AudienceForm` já testado do `Campaigns.jsx` pra montar o
 * filtro, então segue exatamente as mesmas capacidades: `all_customers`,
 * `by_vehicle` (agora com KM), `by_upcoming_service` (com opção projetada
 * pela IA), etc.
 *
 * Convenção cron_expr: POSIX 5 campos. Pré-sets rápidos cobrem 95% dos
 * casos comuns (todo dia às 8h, toda segunda 8h, dia 1 8h, dia 15 8h).
 * Campo custom pra usuário técnico.
 */
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import VariableInserter from "@/components/VariableInserter";
import { api } from "@/lib/api";
import { useSector } from "@/lib/sectorContext";
import {
  CalendarBlank, Play, Pause, Trash, Plus, ArrowClockwise,
} from "@phosphor-icons/react";

const CRON_PRESETS = [
  { key: "daily-8",     label: "Todo dia · 08h",    expr: "0 8 * * *"  },
  { key: "weekly-mon",  label: "Toda segunda · 08h", expr: "0 8 * * MON" },
  { key: "biweekly",    label: "Dia 1 e 15 · 08h", expr: "0 8 1,15 * *" },
  { key: "monthly-1",   label: "Dia 1 do mês · 08h", expr: "0 8 1 * *" },
  { key: "monthly-15",  label: "Dia 15 do mês · 08h", expr: "0 8 15 * *" },
];

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function statusBadge(rec) {
  if (!rec.active && rec.paused_reason?.startsWith("auto-pause")) {
    return <span className="text-[10px] uppercase tracking-wider bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 rounded">Auto-pausada · {rec.consecutive_failures} falhas</span>;
  }
  if (!rec.active) {
    return <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded">Pausada</span>;
  }
  return <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">Ativa</span>;
}

export default function RecurringCampaigns() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/recurring-campaigns");
      setItems(data);
    } catch (e) {
      toast.error("Falha ao carregar recorrências.");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (rec) => {
    try {
      if (rec.active) {
        await api.post(`/recurring-campaigns/${rec.id}/pause`);
        toast.success("Recorrência pausada.");
      } else {
        await api.post(`/recurring-campaigns/${rec.id}/resume`);
        toast.success("Recorrência reativada.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao alternar estado.");
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Excluir "${rec.name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/recurring-campaigns/${rec.id}`);
      toast.success("Recorrência removida.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover.");
    }
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1200px] mx-auto" data-testid="recurring-campaigns-page">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
            Automação
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mt-1">
            Campanhas recorrentes
          </h1>
          <p className="text-[13px] text-slate-500 mt-2 max-w-[600px] leading-relaxed">
            Configure uma vez e a IA dispara automaticamente na frequência definida.
            Use com o filtro &quot;estimado pela IA&quot; pra pescar clientes próximos da
            próxima interação recomendada, baseada em histórico e comportamento.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium px-4 py-2 rounded-md transition-colors"
          data-testid="new-recurring-btn"
        >
          <Plus size={14} weight="regular" /> Nova recorrência
        </button>
      </header>

      {items === null && (
        <div className="bg-white border border-slate-200 rounded-lg py-12 text-center text-slate-500 text-sm">
          Carregando…
        </div>
      )}

      {items && items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg py-16 text-center text-slate-500">
          <CalendarBlank size={28} weight="regular" className="inline-block mb-3 text-slate-400" />
          <div className="text-sm">Nenhuma recorrência configurada ainda.</div>
          <div className="text-[12px] mt-1">
            Clique em &quot;Nova recorrência&quot; e configure sua primeira automação.
          </div>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="space-y-2">
          {items.map((rec) => (
            <div
              key={rec.id}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
              data-testid={`recurring-${rec.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading text-[15px] font-semibold text-slate-900">
                      {rec.name}
                    </div>
                    {statusBadge(rec)}
                  </div>
                  <div className="text-[12px] text-slate-500 mt-1 font-mono">
                    {rec.cron_expr} · {rec.timezone}
                  </div>
                  <div className="text-[12px] text-slate-600 mt-2 line-clamp-2">
                    {rec.message_template}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-[11px]">
                    <div>
                      <div className="uppercase tracking-wider text-slate-400 font-medium">Próximo disparo</div>
                      <div className="text-slate-800 font-medium mt-0.5 tabular-nums">{fmtDateTime(rec.next_run_at)}</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wider text-slate-400 font-medium">Último disparo</div>
                      <div className="text-slate-800 font-medium mt-0.5 tabular-nums">{fmtDateTime(rec.last_run_at)}</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wider text-slate-400 font-medium">Total disparos</div>
                      <div className="text-slate-800 font-medium mt-0.5 tabular-nums">{rec.total_runs || 0}</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wider text-slate-400 font-medium">Alvo total</div>
                      <div className="text-slate-800 font-medium mt-0.5 tabular-nums">{rec.total_targets || 0}</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => toggle(rec)}
                    title={rec.active ? "Pausar" : "Reativar"}
                    className={`w-9 h-9 rounded-md border flex items-center justify-center transition-colors ${rec.active ? "border-slate-200 hover:bg-slate-50" : "border-emerald-200 bg-emerald-50 hover:bg-emerald-100"}`}
                    data-testid={`recurring-toggle-${rec.id}`}
                  >
                    {rec.active ? <Pause size={14} weight="regular" /> : <Play size={14} weight="regular" className="text-emerald-700" />}
                  </button>
                  <button
                    onClick={() => { setEditing(rec); setModalOpen(true); }}
                    title="Editar"
                    className="w-9 h-9 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                    data-testid={`recurring-edit-${rec.id}`}
                  >
                    <ArrowClockwise size={14} weight="regular" />
                  </button>
                  <button
                    onClick={() => remove(rec)}
                    title="Excluir"
                    className="w-9 h-9 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                    data-testid={`recurring-delete-${rec.id}`}
                  >
                    <Trash size={14} weight="regular" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <RecurringModal
          initial={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function RecurringModal({ initial, onClose, onSaved }) {
  // v0.98 · Fev/2026 — placeholders sector-aware.
  const { isAutomotive, labels } = useSector();
  const assetSing = labels?.assetSingular?.() || "veículo";
  const serviceSing = labels?.serviceSingular?.() || "atendimento";
  const _phName = isAutomotive
    ? "ex: Lembrete mensal de troca de óleo"
    : `ex: Lembrete mensal de ${serviceSing}`;
  const _phMsg = isAutomotive
    ? "Oi {nome_cliente}, tá na hora da troca de óleo do seu {veiculo}! Agenda aqui?"
    : `Oi {nome_cliente}, tá chegando a data do seu próximo ${serviceSing}! Quer que eu já agende?`;
  const _phDefaultField = isAutomotive
    ? "projected_next_oil_change_at"
    : "projected_next_service_at";
  const [form, setForm] = useState(() => initial ? {
    name: initial.name || "",
    message_template: initial.message_template || "",
    audience: initial.audience || { mode: "all_customers" },
    cron_expr: initial.cron_expr || "0 8 1 * *",
    timezone: initial.timezone || "America/Sao_Paulo",
    active: initial.active !== false,
    max_consecutive_failures: initial.max_consecutive_failures || 3,
  } : {
    name: "",
    message_template: "Oi {nome_cliente}! ",
    audience: { mode: "by_upcoming_service", service_field: _phDefaultField, date_from: "", date_to: "" },
    cron_expr: "0 8 1 * *",
    timezone: "America/Sao_Paulo",
    active: true,
    max_consecutive_failures: 3,
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const messageRef = useRef(null);

  const previewCron = async () => {
    try {
      const { data } = await api.post("/recurring-campaigns/preview-cron", {
        cron_expr: form.cron_expr, timezone: form.timezone,
      });
      setPreview(data.upcoming);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cron inválido.");
      setPreview(null);
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.message_template.trim() || !form.cron_expr.trim()) {
      toast.error("Preencha nome, mensagem e frequência.");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/recurring-campaigns/${initial.id}`, form);
        toast.success("Recorrência atualizada.");
      } else {
        await api.post("/recurring-campaigns", form);
        toast.success("Recorrência criada.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="recurring-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-[560px] max-h-[90vh] overflow-y-auto p-6"
      >
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
          {initial ? "Editar recorrência" : "Nova recorrência"}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Nome</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="recurring-name-input"
              placeholder={_phName}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Mensagem</label>
            <textarea
              ref={messageRef}
              value={form.message_template}
              onChange={(e) => setForm({ ...form, message_template: e.target.value })}
              rows={3}
              data-testid="recurring-template-input"
              placeholder={_phMsg}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
            <div className="mt-1.5">
              <VariableInserter
                textareaRef={messageRef}
                value={form.message_template}
                onChange={(v) => setForm({ ...form, message_template: v })}
                variables={[
                  { token: "{nome_cliente}", label: "Primeiro nome" },
                  { token: "{veiculo}", label: "Veículo/produto do cliente" },
                  { token: "{marca}", label: "Marca do veículo/produto" },
                ]}
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Frequência</label>
            <div className="mt-1.5 grid grid-cols-2 md:grid-cols-3 gap-1.5">
              {CRON_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setForm({ ...form, cron_expr: p.expr })}
                  data-testid={`recurring-preset-${p.key}`}
                  className={`px-2 py-1.5 border rounded-md text-[11px] font-medium transition-colors ${form.cron_expr === p.expr ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 hover:border-slate-900"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={form.cron_expr}
              onChange={(e) => setForm({ ...form, cron_expr: e.target.value })}
              data-testid="recurring-cron-input"
              placeholder="0 8 * * *"
              className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:border-slate-900"
            />
            <button
              type="button"
              onClick={previewCron}
              className="mt-2 text-[11px] text-slate-600 hover:text-slate-900 underline"
              data-testid="recurring-preview-cron-btn"
            >
              Ver próximos 5 disparos
            </button>
            {preview && (
              <div className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-slate-700 space-y-0.5 tabular-nums">
                {preview.map((iso, i) => (
                  <div key={i}>{fmtDateTime(iso)}</div>
                ))}
              </div>
            )}
          </div>

          <div className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-3 leading-relaxed">
            <strong>Filtro de audiência:</strong> mesmo do modal de campanha comum.
            Recomendo <code className="bg-white px-1 rounded">Por data de troca/revisão · Próxima troca — estimada pela IA</code> +
            janela &quot;próximos 30 dias&quot; — a IA calcula quem tá vencendo baseado em
            quilometragem e histórico de cada carro. Configure a audiência criando
            primeiro uma campanha comum como rascunho, e reuso o filtro daqui na próxima versão.
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="rec-active"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="recurring-active-input"
            />
            <label htmlFor="rec-active" className="text-[13px] text-slate-700">
              Ativa (dispara automaticamente conforme a frequência)
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] text-slate-600 hover:text-slate-900"
            data-testid="recurring-cancel-btn"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="recurring-save-btn"
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium rounded-md disabled:opacity-50"
          >
            {saving ? "Salvando…" : (initial ? "Salvar" : "Criar")}
          </button>
        </div>
      </div>
    </div>
  );
}
